import { BEEF_AI_ACCESS_TOKEN, BEEF_AI_BASE_URL, beefAiGet } from "../common/utils/beefAiClient";
import { fetchTaskRunDetails } from "./missingReports";

export const PRICE_HISTORY_OMITTED_COLLECTION = "price_history_omitted";

const LIST_FIELDS = [
  "id",
  "date_created",
  "code",
  "status",
  "provider",
  "provider_id",
  "task_run_id",
  "workflow_run_id",
  "file_id",
  "raw_product_name",
  "raw_retailer",
  "raw_barcode",
  "raw_price",
  "raw_unit_price",
  "raw_unit_quantity",
  "raw_unit",
  "record_at",
  "metadata",
].join(",");

const SORT_MAP = {
  created_desc: "-date_created",
  created_asc: "date_created",
  retailer_asc: "raw_retailer",
  retailer_desc: "-raw_retailer",
  product_asc: "raw_product_name",
  product_desc: "-raw_product_name",
};

export function priceHistoryOmittedConfigured() {
  return Boolean(BEEF_AI_BASE_URL && BEEF_AI_ACCESS_TOKEN);
}

export function getOmittedStatusChoices() {
  return ["pending", "reviewed", "resolved", "auto_resolved"];
}

export function formatOmittedProviderLabel(provider) {
  const raw = String(provider || "").trim();
  if (!raw) return "";
  const withoutPrefix = raw.replace(/^workflow:/i, "").replace(/[_-]+/g, " ");
  return withoutPrefix
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(" ");
}

export function buildOmittedAssetUrl(fileId) {
  const id = String(fileId || "").trim();
  if (!id || !BEEF_AI_BASE_URL) return "";
  const params = new URLSearchParams();
  if (BEEF_AI_ACCESS_TOKEN) {
    params.set("access_token", BEEF_AI_ACCESS_TOKEN);
  }
  const query = params.toString();
  return `${BEEF_AI_BASE_URL}/assets/${id}${query ? `?${query}` : ""}`;
}

function normalizeMetadata(metadata) {
  if (!metadata) return null;
  if (typeof metadata === "string") {
    try {
      const parsed = JSON.parse(metadata);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch (_) {
      return null;
    }
  }
  return typeof metadata === "object" ? metadata : null;
}

function formatDisplayMoney(value) {
  if (value == null || value === "") return null;
  const number = Number(value);
  if (Number.isNaN(number)) return String(value);
  return `$${number.toFixed(2)}`;
}

function formatDisplayDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

/**
 * Extra plain-language notes for the omitted-detail modal.
 *
 * Metadata on `price_history_omitted` is mostly pipeline audit (barcode-finder traces,
 * duplicate raw fields). Only surface facts that help a non-technical reviewer understand
 * *this* omission code — never dump barcode_resolution outcomes/sources/UUIDs.
 *
 * Written by `WorkflowPricingListener.recordOmission` in the knowledge extension.
 */
export function getOmittedContextNotes(row) {
  const notes = [];
  const meta = normalizeMetadata(row?.metadata);
  const code = row?.code;

  if (!meta || !code) {
    return notes;
  }

  if (code === "no_closest_product") {
    const closestName = String(meta.resolved_product_name || "").trim();
    if (closestName) {
      notes.push(
        `A possible match (“${closestName}”) was found, but it was not safe to use automatically.`
      );
    }
    return notes;
  }

  if (code === "future_record_at") {
    const badDate = formatDisplayDate(meta.record_at);
    if (badDate) {
      notes.push(`The photo date looked like ${badDate}, which is in the future, so it was skipped.`);
    }
    return notes;
  }

  if (code === "no_canonical_retailer") {
    const analysis = String(meta.analysis_retailer || "").trim();
    const gps = meta.gps_retailer == null ? "" : String(meta.gps_retailer).trim();
    const shownRetailer = String(row?.raw_retailer || "").trim();

    if (analysis && analysis.toLowerCase() !== shownRetailer.toLowerCase()) {
      notes.push(`Store name from the photo analysis: ${analysis}.`);
    }
    if (gps) {
      notes.push(`Store suggested from the photo location: ${gps}.`);
    } else if (Object.prototype.hasOwnProperty.call(meta, "gps_retailer")) {
      notes.push("The photo location did not identify a store, so the retailer could not be confirmed.");
    }
    return notes;
  }

  if (code === "invalid_price") {
    const parts = [];
    const priceLabel = formatDisplayMoney(meta.price);
    const unitLabel = formatDisplayMoney(meta.unit_price);
    if (priceLabel) parts.push(`price ${priceLabel}`);
    if (unitLabel) parts.push(`unit price ${unitLabel}`);
    if (parts.length) {
      notes.push(`Saved values were not usable (${parts.join(", ")}).`);
    }
    return notes;
  }

  if (code === "protected_row") {
    if (meta.is_lock) {
      notes.push("A locked price already exists for this product from the same photo run, so nothing new was added.");
    } else if (meta.is_void) {
      notes.push("A voided price already exists for this product from the same photo run, so nothing new was added.");
    } else {
      notes.push("An existing protected price blocked this entry from being added.");
    }
    return notes;
  }

  // unresolved_product / no_product_name / no_record_at / barcode_unsupported:
  // reason text + the field table already cover it. Metadata only repeats raw_retailer
  // or attaches barcode_finder audit traces that are not useful in this UI.
  return notes;
}

/**
 * Prefer task-run source image; fall back to Directus file asset from `file_id`.
 */
export function resolveOmittedSourceImageUrl({ fileId, taskRun } = {}) {
  if (taskRun?.sourceImageUrl) {
    return taskRun.sourceImageUrl;
  }
  return buildOmittedAssetUrl(fileId);
}

export async function fetchOmittedTaskRunDetails(taskRunId) {
  return fetchTaskRunDetails(taskRunId);
}

function buildFilter({ status, code } = {}) {
  const clauses = [];

  if (status && status !== "all") {
    clauses.push({ status: { _eq: status } });
  }

  if (code && code !== "all") {
    clauses.push({ code: { _eq: code } });
  }

  if (!clauses.length) {
    return null;
  }

  return clauses.length === 1 ? clauses[0] : { _and: clauses };
}

/**
 * Paginated `price_history_omitted` rows from the Beef AI Directus host.
 */
export async function fetchPriceHistoryOmittedRows({
  page = 1,
  limit = 10,
  status = "all",
  code = "all",
  sortBy = "created_desc",
} = {}) {
  if (!priceHistoryOmittedConfigured()) {
    throw new Error(
      "Price omitted report is not configured. Set REACT_APP_PRICE_DIRECTUS_URL and REACT_APP_PRICE_DIRECTUS_TOKEN."
    );
  }

  const params = new URLSearchParams();
  params.set("access_token", BEEF_AI_ACCESS_TOKEN);
  params.set("page", String(page));
  params.set("limit", String(limit));
  params.set("sort", SORT_MAP[sortBy] || SORT_MAP.created_desc);
  params.set("fields", LIST_FIELDS);
  params.append("meta[]", "filter_count");

  const filter = buildFilter({ status, code });
  if (filter) {
    params.set("filter", JSON.stringify(filter));
  }

  const json = await beefAiGet(`/items/${PRICE_HISTORY_OMITTED_COLLECTION}?${params.toString()}`);

  return {
    rows: json?.data || [],
    total: json?.meta?.filter_count ?? 0,
  };
}

/**
 * Status + reason-code aggregates for the summary bar.
 */
export async function fetchPriceHistoryOmittedSummary() {
  if (!priceHistoryOmittedConfigured()) {
    throw new Error(
      "Price omitted report is not configured. Set REACT_APP_PRICE_DIRECTUS_URL and REACT_APP_PRICE_DIRECTUS_TOKEN."
    );
  }

  const token = encodeURIComponent(BEEF_AI_ACCESS_TOKEN);
  const [totalRes, statusRes, codeRes] = await Promise.all([
    beefAiGet(
      `/items/${PRICE_HISTORY_OMITTED_COLLECTION}?aggregate[count]=*&access_token=${token}`
    ),
    beefAiGet(
      `/items/${PRICE_HISTORY_OMITTED_COLLECTION}?aggregate[count]=*&groupBy[]=status&access_token=${token}`
    ),
    beefAiGet(
      `/items/${PRICE_HISTORY_OMITTED_COLLECTION}?aggregate[count]=*&groupBy[]=code&access_token=${token}`
    ),
  ]);

  const total = Number(totalRes?.data?.[0]?.count ?? 0);
  const byStatus = {};
  for (const row of statusRes?.data || []) {
    byStatus[row.status] = Number(row.count ?? 0);
  }

  const byCode = {};
  for (const row of codeRes?.data || []) {
    byCode[row.code] = Number(row.count ?? 0);
  }

  const pending = byStatus.pending || 0;
  const resolved = (byStatus.resolved || 0) + (byStatus.auto_resolved || 0);
  const remaining = Math.max(0, total - resolved);

  return {
    total,
    pending,
    resolved,
    remaining,
    reviewed: byStatus.reviewed || 0,
    autoResolved: byStatus.auto_resolved || 0,
    byStatus,
    byCode,
  };
}
